import * as React from 'react';
import { PVAStyleModal, PonderaModal } from '../../inclinationmodals';
import { Style, StyleTag, StyleText } from '../../../models';
import './PVAStyle.css';
import { NotificationService, StyleDataService } from '../../../services';
import { PVAStyleBreakdown } from './PVAStyleBreakdown';
import { BaseRouteProps } from '../../../interfaces';

interface PVAStyleProps extends BaseRouteProps<{}> {
    style: Style;
    brief: string;
    content: string;
    core?: String;
    hideButtons?: boolean;
    useAdditionalButtonsInsteadOfViewAll?: boolean;
    name: string;
    notificationService: NotificationService;
    showSecondaryStyle?: boolean;
    showTraits?: boolean;
    onViewAll?: () => void;
    surveyVersion?: any;
    allStyles?: Style[];
    selectedUserData?: any;
    hideDichotomies?: boolean;
}

interface PVAStyleModalState {
    modalVisible: boolean;
    modalSubVisible: boolean;
    modalsVisible: boolean[];
}

class PVAStyleBase extends React.Component<PVAStyleProps, PVAStyleModalState> {
    private styleDataService = new StyleDataService(this.props.notificationService);

    constructor(props: PVAStyleProps) {
        super(props);

        const styleTexts = this.props.style.styleTexts || [];
        var modalsVisible: boolean[] = [];
        styleTexts.filter((styleText: StyleText) => {
            return styleText.styleTextType === 'additional';
        }).map((styleText: StyleText) => {
            modalsVisible.push(false);
        });

        this.state = {
            modalVisible: false,
            modalsVisible: modalsVisible,
            modalSubVisible: false
        };
    }

    toggleModal() {
        this.setState({ modalVisible: !this.state.modalVisible });
    }

    toggleModals(ind: number) {
        var arr = this.state.modalsVisible;
        arr[ind] = !arr[ind];
        this.setState({
            modalsVisible: arr
        });
    }

    toggleModalSub() {
        this.setState({ modalSubVisible: !this.state.modalSubVisible });
    }

    text(key: string) {
        if (this.props.style.styleTexts) {
            var st = this.props.style.styleTexts.find((s: StyleText) => {
                return s.styleTextType === key;
            });
            if (st) {
                return st.value;
            }
        }
        return '';
    }

    render() {
        const modalProps = {
            show: this.state.modalVisible,
            onHide: this.toggleModal.bind(this),
            title: 'PVA Style - ' + this.props.style,
            content: this.props.content,
            style: this.props.style,
            name: this.props.name,
            selectedUserData: this.props.selectedUserData,
            hideDichotomies: this.props.hideDichotomies !== undefined ? this.props.hideDichotomies : true
        };

        const styleTexts = this.props.style.styleTexts || [];
        const sortedAdditionalStyles = styleTexts.filter((styleText: StyleText) => {
            return styleText.styleTextType === 'additional';
        }).sort((one: StyleText, two: StyleText) => {
            if (one.sort > two.sort) { 
                return 1; 
            } else if (one.sort < two.sort) { 
                return -1; 
            }
            return 0;
        });

        const subCoreStyles = styleTexts.filter((styleText: StyleText) => {
            return styleText.styleTextType === 'subCore';
        });

        var subCore = (
            <small>
                {this.props.style.subCore && <a className="subCoreLink" onClick={() => this.setState({modalSubVisible: true})}>{this.props.style.subCore.name}</a>}
                {!this.props.style.subCore && this.props.core}
            </small>
        );

        if (this.props.style.useSecondaryStyle) {
            subCore = (
                <small>
                    {this.props.style.secondaryStyle && (
                        <a className="subCoreLink" onClick={() => this.setState({modalSubVisible: true})}>{this.props.style.secondaryStyle.name}</a>
                    )}
                </small>);
        }

        const customSurround = (styleText: StyleText): string => {
            return `<div class="custom-content ${styleText.keyName.toLowerCase().trim().replace(' ', '-')}-content">${styleText.value}</div>`;
        };

        const currentStyleName = this.props.style.name.toLowerCase();
        const checkmarkUrl = `url("/images/checkmark-${currentStyleName}.svg")`;
        const checkMarkRedUrl = `url("/images/check-mark-${currentStyleName}.svg")`;

        return (
            <div className={`component-PVAStyle pva-style`}>
                <div className="pva-style-title">
                    <img src={`/images/badges/${this.props.style.name.toLowerCase()} icon.svg`}/>
                    <div className="intro font-heading">PVA Style:</div>
                    <div className="style-name font-heading">
                        {this.props.style.name}
                    </div>
                    {this.props.showSecondaryStyle && this.props.style.secondaryStyle && (
                        <div className="secondary-style-name font-heading">
                            & {this.props.style.secondaryStyle.name}
                        </div>
                    )}
                </div>
                <div className="pva-style-content">
                    {this.props.showTraits && (
                        <PVAStyleBreakdown
                            style={this.props.style}
                            brief={this.text('brief')}
                            content={this.props.content}
                            name={this.props.name}
                            showSecondaryStyle={this.props.showSecondaryStyle || false}
                            noPadding={true}
                            hideBrief={false}
                        />
                    )}
                    {!this.props.showTraits && (
                        <React.Fragment>
                            <div className="font-second-level" dangerouslySetInnerHTML={{ __html: this.text('brief')}} />
                            {!this.props.hideButtons && <div className="button-row-wrapper">
                                <div className={"button-row " + (!this.props.useAdditionalButtonsInsteadOfViewAll ? "small-buttons " : "")}>
                                    <button className="btn btn-primary" onClick={(e) => this.setState({modalVisible: true})}>
                                        Read more
                                    </button>
                                    { 
                                        this.props.useAdditionalButtonsInsteadOfViewAll && sortedAdditionalStyles.map((styleText: StyleText) => {
                                            return <button key={styleText.id} className="btn btn-secondary" onClick={() => this.toggleModals(styleTexts.indexOf(styleText))}>{styleText.keyName}</button>; 
                                        })
                                    }
                                    { 
                                        !this.props.useAdditionalButtonsInsteadOfViewAll && this.props.onViewAll && <button className="btn btn-secondary" onClick={() => this.props.onViewAll!()}>View all PVA styles</button>
                                    }
                                </div>
                            </div>}
                        </React.Fragment>
                    )}
                </div>

                <PVAStyleModal 
                    notificationService={this.props.notificationService} 
                    history={this.props.history}
                    location={this.props.location}
                    match={this.props.match}
                    {...modalProps} 
                    onHide={() => this.toggleModal()} 
                    topic={'PVA Style'} 
                    surveyVersion={this.props.surveyVersion}
                    allStyles={this.props.allStyles}
                />

                {this.props.useAdditionalButtonsInsteadOfViewAll && sortedAdditionalStyles.map((styleText: StyleText) => {
                    return <div key={styleText.id} className={`custom-content ${styleText.keyName}-content`}>
                        <PonderaModal 
                            key={styleText.id} 
                            style={this.props.style} 
                            show={this.state.modalsVisible[styleTexts.indexOf(styleText)]} 
                            title={styleText.keyName} 
                            content={customSurround(styleText)} 
                            onHide={(e: any) => this.toggleModals(styleTexts.indexOf(styleText))} 
                            activeColor={`var(--pva-${this.props.style.name.toLowerCase()})`}
                            customContentColor={`var(--pva-${this.props.style.name.toLowerCase()})`}
                            checkmarkUrl={checkmarkUrl}
                            checkMarkRedUrl={checkMarkRedUrl}
                        />
                    </div>
                })}
            </div>);
    }
}
export const PVAStyle = PVAStyleBase;


// WEBPACK FOOTER //
// ./src/components/reports/blocks/PVAStyle.tsx